import { AppLayout } from 'AWS-UI-Components-React';
import { logger } from 'AWS-UI-Components-React/polaris/internal/core/runtime/src';
import React from 'react';
import { REPORT_TYPE } from '../constants/report_type';
import { NavigationHeaderStyle } from '../styles/common_styles.jsx';
import { PhoenixInfo } from '../utils/phoenix_info';
import { AccuracyContent } from './content/accuracy_content';
import { getJSClient } from './js_client';
import { ReportAttributes } from './navigation/report_attributes';

export const Status = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
};

export class Main extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      user: '',
      formSubmitted: false,
      newReport: '',
      columnSetsToLoad: [],
      columnSetOverridesToLoad: [],
      reportsList: [],
      columnsJson: '',
      hierarchy: '',
      authenticated: false,
      loadDone: false,
      preferencesStatus: Status.INIT,
      preferences: {},
      nonAggregatedPayloads: {},
      nonAggregatedFlags: {},
      marketplaceConfig: [],
      productLines: null,
      toolsOpen: false,
      toolsContent: <PhoenixInfo/>,
    };
    this.tablePayload = {};
  }

  componentWillMount = () => {
    document.title = 'Phoenix : Analytics';
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentDidUpdate = () => {
    if (this.state.authenticated && this.state.preferencesStatus === Status.INIT && this.state.columnsJson) {
      this.getUserPreferences();
    }
  };

  initialClientTasks = () => {
    this.getColumnSets();
    this.pullOPPUHierarchy();
    this.loadMarketplaceConfig();
    this.getProductLines();
  };

  setUser = (userID) => {
    this.setState({ user: userID });
  };

  onLoadDone = ({ reportType }) => {
    this.setState({ loadDone: true });
    this.props.recordTrackedEvent({ loadedReportType: reportType });
  };

  getPayload = (payload, snapshots) => {
    this.tablePayload = payload;
    this.snapshots = snapshots;
    this.setState({ formSubmitted: true, loadDone: false });
  };

  resetTable = () => {
    this.setState({ formSubmitted: false });
    this.props.recordTrackedEvent({ loadedReportType: null });
  };

  updateReportsDropdown = (report) => {
    this.setState({ newReport: report });
  };

  setColumnSetsToLoad = (columnSets) => {
    this.setState({ columnSetsToLoad: columnSets });
  };

  setColumnSetOverridesToLoad = (columnSetOverrides) => {
    this.setState({ columnSetOverridesToLoad: columnSetOverrides });
  };

  setReportsList = (reports) => {
    this.setState({ reportsList: reports });
  };

  onUserAuthentication = () => {
    this.setState({ authenticated: true });
  };

  getColumnSets = () => {
    if (!this.state.columnsJson) {
      this.client.pullColumnConfig(
        (columnsResult) => {
          this.setState({ columnsJson: JSON.parse(columnsResult.data) });
        },
        (error) => {
          console.log(error);
        }
      );
    }
  };

  pullOPPUHierarchy = () => {
    if (!this.state.hierarchy) {
      this.client.pullOPPUHierarchy(
        (hierarchyResult) => {
          this.setState({ hierarchy: JSON.parse(hierarchyResult.data) });
        },
        (error) => {
          console.log(error);
        }
      );
    }
  };

  getProductLines = () => {
    this.client.getPlHierarchy(
      (result) => {
        this.setState({ productLines: result.root });
      },
      (error) => {
        console.log(error);
      },
      {} // Empty query paramenters - defaults should be sufficient.
    );
  };

  getUserPreferences = () => {
    this.setState({ preferencesStatus: Status.LOADING });
    this.client.pullUserPreferences(
      (userPreferences) => {
        this.checkForInvalidColumnsAndColumnSets(userPreferences.savedPreferences);
      },
      () => {
        this.setState({ preferencesStatus: Status.FAILED });
      },
      this.state.user
    );
  };

  setUserPreferences = (savedPreferences) => {
    this.setState({ preferences: savedPreferences, preferencesStatus: Status.SUCCEEDED });
  };

  checkForInvalidColumnsAndColumnSets = (savedPreferences) => {
    console.log('Checking for invalid columns and column sets in user preferences');
    const componentColumns = this.state.columnsJson.componentSets[0]['columns'];
    const plColumns = this.state.columnsJson.plSets[0]['columns'];
    const componentColumnSets = this.state.columnsJson.componentSets.map((set) => set.id);
    const plColumnSets = this.state.columnsJson.plSets.map((set) => set.id);

    let savePreferencesPayload = {};
    savePreferencesPayload['preferencesToSave'] = savedPreferences;
    let invalidateFlag = false;
    if (
      savePreferencesPayload['preferencesToSave'] &&
      savePreferencesPayload['preferencesToSave']['reportPreferences']
    ) {
      for (let pref of savePreferencesPayload['preferencesToSave']['reportPreferences']) {
        let validColumns = [];
        let validColumnSets = [];
        if (pref.reportType === REPORT_TYPE.COMPONENT.toLowerCase()) {
          validColumns = pref['columns'].filter((col) => componentColumns.includes(col));
          validColumnSets = pref['columnSets'].filter((set) => componentColumnSets.includes(set));
        } else if (pref.reportType === REPORT_TYPE.PL.toLowerCase()) {
          validColumns = pref['columns'].filter((col) => plColumns.includes(col));
          validColumnSets = pref['columnSets'].filter((set) => plColumnSets.includes(set));
        }
        if (validColumns.length !== pref['columns'].length || validColumnSets.length !== pref['columnSets'].length) {
          console.log(
            'Invalidating %i column(s) from report: %s',
            pref['columns'].length - validColumns.length,
            pref.reportType
          );
          console.log(
            'Invalidating %i columnSet(s) from report: %s',
            pref['columnSets'].length - validColumnSets.length,
            pref.reportType
          );
          invalidateFlag = true;
          pref['columns'] = validColumns;
          pref['columnSets'] = validColumnSets;
        } else {
          console.log(
              'Column(s) from report: %s are validated',
              pref.reportType,
              validColumns,
              validColumnSets
          );
        }
      }
      if (invalidateFlag) {
        this.client.saveUserPreferences(
          () => {
            this.setUserPreferences(savePreferencesPayload['preferencesToSave']);
          },
          this.resetTable(),
          savePreferencesPayload,
          this.state.user
        );
      }
    }
    this.setState({ preferences: savedPreferences, preferencesStatus: Status.SUCCEEDED });
  };

  onSnapshotsLoad = (snapshots) => {
    this.setState({ snapshots });
  };

  /**
   * Checks if user selected snapshots are available otherwise gets live snapshot
   */
  getCurrentSnapshots = () => {
    if (this.snapshots) {
      return this.snapshots;
    } else {
      if (this.props.snapshots) {
        return this.props.snapshots[this.props.snapshots.map((o) => o.id).indexOf(this.props.appConfig.liveSnapshotId)];
      } else {
        return this.state.snapshots;
      }
    }
  };

  liveSnapshotCallback = (liveSnapshot) => {
    this.setState({ liveSnapshot });
  };

  loadMarketplaceConfig = () => {
    this.client.getMarketplaceConfig(this.onMarketplaceConfigFetchSuccess);
  };

  onMarketplaceConfigFetchSuccess = (response) => {
    const marketplaceConfig = JSON.parse(response.marketplaceConfig);
    this.setState({ marketplaceConfig });
  };

  onMarketplaceConfigFetchFailure = (error) => {
    logger.error('Error fetching marketplace config', error);
  };

  setNonAggregatedPayloads = (nonAggregatedPayloads) => {
    this.setState({ nonAggregatedPayloads: nonAggregatedPayloads });
  };

  setNonAggregatedFlags = (nonAggregatedFlags) => {
    this.setState({ nonAggregatedFlags: nonAggregatedFlags });
  };

  Content = () => (
    <div className="awsui-util-p-s">
      <div className="custom-home__header custom-awsui-util-pt-xxxl awsui-row">
        <div className="col-xxs-12">
          <div className="awsui-row">
            <div className="custom-home__header-title col-xxs-10 offset-xxs-1 col-s-6 col-l-5 offset-l-2 col-xl-6">
              <div className="awsui-row">
                <div className="awsui-row">
                  {
                    <AccuracyContent
                      user={this.state.user}
                      identity={this.props.identity}
                      featureList={this.props.featureList}
                      submitted={this.state.formSubmitted}
                      payload={this.tablePayload}
                      snapshots={this.getCurrentSnapshots()}
                      onSaveReport={this.updateReportsDropdown}
                      onLoadDone={this.onLoadDone}
                      loadDone={this.state.loadDone}
                      columnSetsToLoad={this.state.columnSetsToLoad}
                      columnSetOverridesToLoad={this.state.columnSetOverridesToLoad}
                      reportsList={this.state.reportsList}
                      columnsJson={this.state.columnsJson}
                      hierarchy={this.state.hierarchy}
                      appConfig={this.props.appConfig}
                      liveSnapshotConfig={this.state.liveSnapshot}
                      savedPreferences={this.state.preferences}
                      OnSavePreferences={this.setUserPreferences}
                      nonAggregatedPayloads={this.state.nonAggregatedPayloads}
                      nonAggregatedFlags={this.state.nonAggregatedFlags}
                      marketplaceItems={this.state.marketplaceConfig}
                      productLines={this.state.productLines}
                      updateTools={ (newToolsContent) => {
                        this.setState({ toolsContent: newToolsContent, toolsOpen: true });
                      }}
                    />
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  Navigation = () => (
    <div data-navigation-level="0">
      <div className="website-navigation-title awsui-util-font-size-3" style={NavigationHeaderStyle}>
        Report Attributes
      </div>
      <ReportAttributes
        featureList={this.props.featureList}
        callback={this.getPayload}
        defineUser={this.setUser}
        setColumnSetsToLoad={this.setColumnSetsToLoad}
        setColumnSetOverridesToLoad={this.setColumnSetOverridesToLoad}
        resetFunction={this.resetTable}
        newReport={this.state.newReport}
        loadID={this.props.match.params.id}
        setReportsList={this.setReportsList}
        identity={this.props.identity}
        onUserAuthentication={this.onUserAuthentication}
        authenticated={this.state.authenticated}
        snapshotsLoadCallback={this.onSnapshotsLoad}
        liveSnapshotCallback={this.liveSnapshotCallback}
        appConfig={this.props.appConfig}
        nonAggregatedPayloads={this.setNonAggregatedPayloads}
        nonAggregatedFlags={this.setNonAggregatedFlags}
        marketplaceConfig={this.state.marketplaceConfig}  
        productLines={this.state.productLines}
        queryParameters={this.props.queryParameters}      
      />
    </div>
  );

  render() {
    return (
      <div>
        <AppLayout
          key={(this.state.productLines || []).length}
          className="awsui-util-no-gutters"
          content={this.Content()}
          navigation={this.Navigation()}
          footerSelector="#f"
          tools={this.state.toolsContent}
          toolsOpen={this.state.toolsOpen}
          navigationHide={ false }
          onToolsChange={ ({detail}) => this.state.toolsOpen = detail.open }
        />
        <div id="f">
          <div id="console-nav-footer" className="Footer">
            Highly Confidential Data
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/main.jsx